import React from 'react';
import PropTypes from 'prop-types';
import { Box, Stack, Typography } from '@mui/material';
import { CheckCircle } from '@mui/icons-material';

/**
 * OTRIX Play — large selectable card for the onboarding wizard (objectives, location, …).
 * Neutral gray fill when selected (single-accent rule: gold only on the small check),
 * following the OTRIX system (200ms cubic-bezier, active scale).
 */
function OnboardingOptionCard({ icon = null, title, description = '', selected = false, onClick }) {
  return (
    <Box
      role="button"
      aria-pressed={selected}
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      sx={{
        position: 'relative',
        height: '100%',
        p: 2.5,
        borderRadius: '16px',
        cursor: 'pointer',
        bgcolor: selected ? 'selected.bg' : 'surface.raised',
        border: '1px solid',
        borderColor: selected ? 'hairline.strong' : 'hairline.main',
        boxShadow: 'inset 0 1px 0 var(--otx-inner-highlight)',
        transition: 'transform 220ms cubic-bezier(0.32,0.72,0,1), border-color 220ms, background-color 220ms',
        '&:hover': { transform: 'translateY(-2px)', borderColor: 'hairline.strong' },
        '&:active': { transform: 'scale(0.98)' }
      }}>
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        {icon && (
          <Box sx={{ color: selected ? 'selected.fg' : 'text.secondary', mt: 0.25, display: 'flex' }}>{icon}</Box>
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 800, letterSpacing: '-0.01em' }}>{title}</Typography>
          {description && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {description}
            </Typography>
          )}
        </Box>
        <CheckCircle
          sx={{
            color: 'gold.main',
            flex: 'none',
            opacity: selected ? 1 : 0,
            transform: selected ? 'scale(1)' : 'scale(0.6)',
            transition: 'opacity 200ms, transform 200ms cubic-bezier(0.32,0.72,0,1)'
          }}
        />
      </Stack>
    </Box>
  );
}

OnboardingOptionCard.propTypes = {
  icon: PropTypes.node,
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  selected: PropTypes.bool,
  onClick: PropTypes.func.isRequired
};

export default OnboardingOptionCard;
